import React, { useState, useEffect } from 'react';
import { FiSearch, FiCopy, FiPackage } from 'react-icons/fi';
import Modal from '../UI/Modal';
import { LoadingGrid } from '../UI/Loading';
import { productosService, formulacionesService } from '../../services';

const DuplicarFormulacionModal = ({ isOpen, onClose, onSelectFormulacion }) => {
  const [productos, setProductos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducto, setSelectedProducto] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadProductosConFormulacion();
    }
  }, [isOpen]);

  const loadProductosConFormulacion = async () => {
    setLoading(true);
    try {
      // Obtener todos los productos
      const response = await productosService.getProductos();

      if (response.success && response.data) {
        // Filtrar solo los que tienen formulación
        const productosConFormulacion = response.data.filter(
          producto => producto.has_formulation === true
        );
        setProductos(productosConFormulacion);
      }
    } catch (error) {
      console.error('Error cargando productos con formulación:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProducto = async (producto) => {
    setSelectedProducto(producto);
    setLoading(true);

    try {
      // Obtener la formulación del producto seleccionado
      const response = await formulacionesService.obtenerFormulacionPorProducto(producto.id);

      if (response.success && response.data) {
        const formulaciones = response.data.formulations || [];

        if (formulaciones.length > 0) {
          const formulacion = formulaciones[0]; // Tomar la primera formulación

          // Pasar la formulación al componente padre
          onSelectFormulacion(formulacion);
          onClose();
        } else {
          alert('Este producto no tiene formulación disponible');
        }
      }
    } catch (error) {
      console.error('Error cargando formulación:', error);
      alert('Error al cargar la formulación del producto seleccionado');
    } finally {
      setLoading(false);
      setSelectedProducto(null);
    }
  };

  // Filtrar productos por búsqueda
  const productosFiltrados = productos.filter(producto =>
    producto.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    producto.code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Duplicar Formulación de Otro Producto"
      size="large"
    >
      <div style={{ padding: '0 8px' }}>
        {/* Buscador */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ position: 'relative' }}>
            <FiSearch style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#888',
              fontSize: '18px'
            }} />
            <input
              type="text"
              className="form-input"
              placeholder="Buscar por nombre o código del producto..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '40px' }}
              autoFocus
            />
          </div>
        </div>

        {/* Lista de productos */}
        {loading ? (
          <LoadingGrid />
        ) : productosFiltrados.length > 0 ? (
          <div style={{
            maxHeight: '400px',
            overflowY: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: '8px'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{
                position: 'sticky',
                top: 0,
                backgroundColor: '#7B2D8E',
                color: 'white',
                zIndex: 1
              }}>
                <tr>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #6a2679' }}>
                    Producto
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #6a2679' }}>
                    Código
                  </th>
                  <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #6a2679' }}>
                    Acción
                  </th>
                </tr>
              </thead>
              <tbody>
                {productosFiltrados.map((producto) => (
                  <tr
                    key={producto.id}
                    style={{
                      borderBottom: '1px solid #e0e0e0',
                      backgroundColor: selectedProducto?.id === producto.id ? '#f0e6f3' : 'white',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (selectedProducto?.id !== producto.id) {
                        e.currentTarget.style.backgroundColor = '#f9f9f9';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedProducto?.id !== producto.id) {
                        e.currentTarget.style.backgroundColor = 'white';
                      }
                    }}
                  >
                    <td style={{ padding: '12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <FiPackage style={{ color: '#7B2D8E', fontSize: '18px' }} />
                        <span style={{ fontWeight: '500' }}>{producto.name}</span>
                      </div>
                    </td>
                    <td style={{ padding: '12px', color: '#666' }}>
                      {producto.code || 'N/A'}
                    </td>
                    <td style={{ padding: '12px', textAlign: 'center' }}>
                      <button
                        className="btn btn--sm btn--primary"
                        onClick={() => handleSelectProducto(producto)}
                        disabled={selectedProducto?.id === producto.id}
                        style={{
                          backgroundColor: '#7B2D8E',
                          borderColor: '#7B2D8E',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <FiCopy /> Duplicar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#888',
            backgroundColor: '#f9f9f9',
            borderRadius: '8px'
          }}>
            {searchTerm ? (
              <>
                <FiSearch style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.5 }} />
                <p>No se encontraron productos con el término "{searchTerm}"</p>
              </>
            ) : (
              <>
                <FiPackage style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.5 }} />
                <p>No hay productos con formulación disponibles</p>
              </>
            )}
          </div>
        )}

        {/* Footer con información */}
        <div style={{
          marginTop: '20px',
          padding: '12px',
          backgroundColor: '#f0f7ff',
          borderRadius: '6px',
          border: '1px solid #b3d9ff',
          fontSize: '0.9em',
          color: '#0066cc'
        }}>
          <strong>Nota:</strong> Al duplicar una formulación, todos los datos (ingredientes, cantidades, configuración)
          se copiarán al producto actual. Podrás modificar lo que necesites antes de finalizar.
        </div>
      </div>
    </Modal>
  );
};

export default DuplicarFormulacionModal;
